import React, { useState } from 'react';
import { z } from 'zod';
import {
  useForm,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from './Form';
import { Input } from './Input';
import { Button } from './Button';
import { useChangePassword } from '../helpers/useUserProfileApi';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import styles from './UserSettingsChangePasswordForm.module.css';

const passwordFormSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type PasswordFormValues = z.infer<typeof passwordFormSchema>;

export function UserSettingsChangePasswordForm() {
  const { mutate: changePassword, isPending } = useChangePassword();

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    schema: passwordFormSchema,
  });

  const onSubmit = (values: PasswordFormValues) => {
    changePassword(
      {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      },
      {
        onSuccess: () => {
          toast.success('Password changed successfully');
          form.setValues({
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          });
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to change password');
        },
      },
    );
  };

  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>Change Password</h3>
      <p className={styles.cardDescription}>
        Update your password securely by providing your current password.
      </p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className={styles.form}>
          <FormItem name="currentPassword">
            <FormLabel>Current Password</FormLabel>
            <div className={styles.passwordInputWrapper}>
              <FormControl>
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.values.currentPassword}
                  onChange={(e) =>
                    form.setValues((prev) => ({ ...prev, currentPassword: e.target.value }))
                  }
                />
              </FormControl>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className={styles.toggleButton}
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                tabIndex={-1}
              >
                {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </Button>
            </div>
            <FormMessage />
          </FormItem>

          <FormItem name="newPassword">
            <FormLabel>New Password</FormLabel>
            <div className={styles.passwordInputWrapper}>
              <FormControl>
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.values.newPassword}
                  onChange={(e) =>
                    form.setValues((prev) => ({ ...prev, newPassword: e.target.value }))
                  }
                />
              </FormControl>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className={styles.toggleButton}
                onClick={() => setShowNewPassword(!showNewPassword)}
                tabIndex={-1}
              >
                {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </Button>
            </div>
            <FormDescription>Must be at least 8 characters long.</FormDescription>
            <FormMessage />
          </FormItem>

          <FormItem name="confirmPassword">
            <FormLabel>Confirm New Password</FormLabel>
            <div className={styles.passwordInputWrapper}>
              <FormControl>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.values.confirmPassword}
                  onChange={(e) =>
                    form.setValues((prev) => ({ ...prev, confirmPassword: e.target.value }))
                  }
                />
              </FormControl>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className={styles.toggleButton}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </Button>
            </div>
            <FormMessage />
          </FormItem>

          <div className={styles.actions}>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Changing Password...' : 'Change Password'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
